import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFloating, useDismiss, useRole, useInteractions } from '@floating-ui/react';
import { DialogProps, DialogSize } from './types';

export const sizeToDimension = (
  size: DialogSize | { width?: string; maxWidth?: string; minWidth?: string }
): { width?: string; maxWidth?: string; minWidth?: string } => {
  if (typeof size !== 'string') {
    return size;
  }

  switch (size) {
    case DialogSize.small:
      return { width: '540px' };
    case DialogSize.large:
      return { width: '800px' };
    case DialogSize.xlarge:
      return { width: '80vw' };
    case DialogSize.medium:
    default:
      return { width: '700px' };
  }
};

export function useDialog({
  color,
  defaultOpen,
  hasCloseButton,
  initialFocus,
  isOpen: propsIsOpen,
  onOpen,
  size,
  triggerClassName,
}: Omit<DialogProps, 'children'>) {
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const actionsRef = useRef(null);
  const [labelId, setLabelId] = React.useState<string | undefined>();
  const [descriptionId, setDescriptionId] = React.useState<string | undefined>();

  const [isOpen, setIsOpen] = useState(propsIsOpen ?? defaultOpen);

  const { width, minWidth, maxWidth } = sizeToDimension(size!);

  useEffect(() => {
    setIsOpen(propsIsOpen ?? defaultOpen);
  }, [propsIsOpen, defaultOpen]);

  const handleOpen = useCallback(
    (collapsed: boolean) => {
      if (onOpen) {
        onOpen(collapsed);
      }
      if (propsIsOpen === undefined || propsIsOpen === null) {
        setIsOpen(collapsed);
      }
    },
    [onOpen]
  );

  const data = useFloating({
    open: isOpen,
    onOpenChange: handleOpen,
  });

  const context = data.context;

  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' });
  const role = useRole(context);

  const interactions = useInteractions([dismiss, role]);

  return React.useMemo(
    () => ({
      color,
      isOpen,
      handleOpen,
      ...interactions,
      ...data,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
      hasCloseButton,
      initialFocus,
      width,
      minWidth,
      maxWidth,
      headerRef,
      bodyRef,
      actionsRef,
      triggerClassName,
    }),
    [
      color,
      isOpen,
      handleOpen,
      interactions,
      data,
      labelId,
      descriptionId,
      hasCloseButton,
      initialFocus,
      width,
      minWidth,
      maxWidth,
      headerRef,
      bodyRef,
      actionsRef,
      triggerClassName,
    ]
  );
}
